import { put, takeLatest, select, take, call, all } from 'redux-saga/effects';
import * as Comlink from 'comlink';

import {
  isGenericGb,
  hasDiscoverTaxonomy,
  isGenericUs,
  isWwGwiMed,
} from '@discover-frontend/utils';
import { ADDON_SLUGS } from '@discover-frontend/constants';
import { isEmpty } from 'src/utils';
import { PSYCHOGRAPHIC_PREFIX } from 'src/constants/config';
import TaxonomyWorker from '../workers/taxonomy.worker';

import {
  GET_TAXONOMY_LIST_REQUEST,
  GET_TAXONOMY_LIST_SUCCESS,
  GET_BASE_TAXONOMY,
  GET_DEMOGRAPHICS_SUCCESS,
  GET_BASE_TAXONOMY_WITH_LIFESTYLES,
} from '../constants/action-types';

import * as api from '../services/engineApi';
import { getSources, isAvailableAddon } from '../utils/AuthHelperMethods';
import { getCurrentProductDataSources } from '../selectors/myCompany';

function* fetchTaxonomyList(action) {
  const { types } = action;
  const productDataSources = yield select((state) =>
    getCurrentProductDataSources(state)
  );
  const taxonomyList = yield select((state) => state.taxonomyList.data);

  if (Object.keys(taxonomyList).length > 0) {
    return yield put({
      type: GET_TAXONOMY_LIST_SUCCESS,
      response: taxonomyList,
    });
  }
  let dataSources = [];
  let promises = [];

  if (!isEmpty(productDataSources)) {
    dataSources = dataSources.concat(...productDataSources);
  }
  const [
    { response: taxonomyTwitterResponse },
    { response: nativeTaxonomyResponse },
  ] = yield all([
    call(api.fetchTaxonomyApi, types, getSources('twitter')),
    call(api.fetchTaxonomyApi, types, 'twitter'),
  ]);

  let allTaxonomy = {};
  if (!isEmpty(dataSources)) {
    promises = dataSources.map((dataSource) => {
      const sources = getSources(dataSource);
      return call(
        api.fetchTaxonomyApi,
        ['demographic', 'item', 'category'],
        sources
      );
    });

    const allTaxonomyResponse = yield all(promises);
    allTaxonomy = allTaxonomyResponse.reduce((acc, result, i) => {
      const { response: taxonomy_response, error: taxonomy_error } = result;

      if (!taxonomy_error) {
        acc[dataSources[i]] = taxonomy_response.data;
      } else {
        acc[dataSources[i]] = [];
      }
      return acc;
    }, {});
  }

  allTaxonomy = {
    ...allTaxonomy,
    twitter: (taxonomyTwitterResponse && taxonomyTwitterResponse.data) || [],
  };

  const nativeTaxonomy = {
    ...allTaxonomy,
    twitter: (nativeTaxonomyResponse && nativeTaxonomyResponse.data) || [],
  };

  dataSources = ['twitter', ...dataSources];

  let demographicsItems = yield select((state) => state.demographicsItems.data);
  if (!Object.keys(demographicsItems).length) {
    yield take(GET_DEMOGRAPHICS_SUCCESS);
    demographicsItems = yield select((state) => state.demographicsItems.data);
  }

  const taxonomyWorker = Comlink.wrap(new TaxonomyWorker());

  promises = dataSources.map((dataSource) => {
    return call(taxonomyWorker.getBaseTaxonomy, {
      taxonomy: nativeTaxonomy[dataSource],
      demographicsItems: demographicsItems[dataSource],
      hasDiscoverTaxonomy: hasDiscoverTaxonomy(),
      isGenericGb: isGenericGb(),
      isGenericUs: isGenericUs(),
      hasUsPopulation21Plus: isAvailableAddon(ADDON_SLUGS.US_POPULATION_21_PLUS),
      isWwGwiMed: isWwGwiMed(),
      hasMriSimmonsFusion: false,
      hasGwiFusion: false,
      hasWWGwiMediaFusion: false,
      hasTgiFusion: false,
    });
  });

  const allBaseTaxonomyResponse = yield all(promises);

  const allBaseTaxonomy = allBaseTaxonomyResponse.reduce((acc, result, i) => {
    acc[dataSources[i]] = {
      baseTaxonomy: result,
      lifestyles: result.flat().filter((e) => e.parent_id === 'lifestyle'),
    };
    return acc;
  }, {});

  promises = Object.entries(allBaseTaxonomy)
    .map(([dataSource, baseTaxonomyAndLifestyles]) => {
      return baseTaxonomyAndLifestyles.lifestyles.map((lifestyle) => {
        return call(
          api.fetchExploreItemsApi,
          `?category_id=${lifestyle.id}&variable_types=item,category,psychographic,demographic&page=1&limit=20&sources=${dataSource}`
        );
      });
    })
    .flat();

  const allLifestylesResponse = yield all(promises);

  const lifestylesVariablesResponse = {};
  let i = 0;
  Object.entries(allBaseTaxonomy).forEach(
    ([dataSource, baseTaxonomyAndLifestyles]) => {
      lifestylesVariablesResponse[dataSource] = allLifestylesResponse.slice(
        i,
        baseTaxonomyAndLifestyles.lifestyles.length
      );
      i += baseTaxonomyAndLifestyles.lifestyles.length;
    }
  );

  const allBaseTaxonomyWithLifestyles = Object.entries(allBaseTaxonomy).reduce(
    (acc, [dataSource, baseTaxonomyAndLifestyles], i) => {
      const { baseTaxonomy, lifestyles } = baseTaxonomyAndLifestyles;

      let lifestyleLevel;

      baseTaxonomy.forEach((l, i) => {
        if (l.find((e) => e.id === 'lifestyle')) {
          lifestyleLevel = i + 2;
        }
      });
      const lifestylesVariables = lifestylesVariablesResponse[dataSource].map(
        (l, i) =>
          l.response.data.map((e) => ({
            id: `${PSYCHOGRAPHIC_PREFIX}${e.id}`,
            label: e.label,
            parent_id: lifestyles[i].id,
            pos: lifestyleLevel,
            type: e.type,
          }))
      );

      acc[dataSource] = baseTaxonomy
        .map((l, i) => {
          if (lifestyleLevel === i) {
            return [...l, ...lifestylesVariables.flat()];
          } else {
            return l;
          }
        })
        .map((l) =>
          l.map((e) => ({
            ...e,
            id: e.id,
          }))
        );

      return acc;
    },
    {}
  );
  yield put({
    type: GET_BASE_TAXONOMY,
    message: Object.entries(allBaseTaxonomy).reduce(
      (acc, [dataSource, baseTaxonomyAndLifestyles], i) => {
        acc[dataSource] = baseTaxonomyAndLifestyles.baseTaxonomy;
        return acc;
      },
      {}
    ),
  });
  yield put({
    type: GET_BASE_TAXONOMY_WITH_LIFESTYLES,
    message: allBaseTaxonomyWithLifestyles,
  });

  return yield put({
    type: GET_TAXONOMY_LIST_SUCCESS,
    response: allTaxonomy,
  });
}

export function* taxonomyActionWatcher() {
  yield takeLatest(GET_TAXONOMY_LIST_REQUEST, fetchTaxonomyList);
}
